// MST
import { types, flow, cast, detach } from 'mobx-state-tree'

// Interfaces
import { ICertificationWebpageProcessDate } from 'interfaces/webpage/certification/registration/process-date'
import { ICertificationWebpageProcessDateApiResponse } from 'interfaces/webpage/certification/registration/process-date/process-date-api.interface'
import {
  ICertificationWebpageProcessDateGetDetailAttrs,
  ICertificationWebpageProcessDateUpdateAttrs,
} from 'interfaces/webpage/certification/registration/process-date/process-date-view.interface'
import { ISharedCommonLoadingAttrs } from 'interfaces/shared/shared-common.interface'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { withHelper_commonLoadingModel, withHelper_commonMapLoading } from 'models/extensions/helper'

// Constant
import { LOADING } from 'constant/shared.constant'

// Types
const { model, optional, string, maybeNull } = types

// Loading
const { BASE, ACTION } = LOADING

const CertificationProcessDate = model('CertificationProcessDate').props({
  personalData: maybeNull(string),
  purposeOfAssessment: maybeNull(string),
  precondition: maybeNull(string),
  selfAssessment: maybeNull(string),
  final: maybeNull(string),
})

const CertificationWebpageProcessDateModel = model('CertificationWebpageProcessDate')
  .props({
    certificationWebpageProcessDate_loading: withHelper_commonLoadingModel,
    certificationWebpageProcessDate_detail: optional(CertificationProcessDate, {}),
  })
  .actions(self => ({
    certificationWebpageProcessDate_SET_LOADING: (payload: ISharedCommonLoadingAttrs): void => {
      const map = withHelper_commonMapLoading(self.certificationWebpageProcessDate_loading, payload)

      detach(self.certificationWebpageProcessDate_loading)
      self.certificationWebpageProcessDate_loading = cast(map)
    },
    certificationWebpageProcessDate_SET_DETAIL: (payload: ICertificationWebpageProcessDate): void => {
      self.certificationWebpageProcessDate_detail = cast({
        personalData: payload.personalData ? dateUtils_formatDate(payload.personalData, { tableFormat: true }) : null,
        purposeOfAssessment: payload.purposeOfAssessment
          ? dateUtils_formatDate(payload.purposeOfAssessment, { tableFormat: true })
          : null,
        precondition: payload.precondition ? dateUtils_formatDate(payload.precondition, { tableFormat: true }) : null,
        selfAssessment: payload.selfAssessment ? dateUtils_formatDate(payload.selfAssessment, { tableFormat: true }) : null,
        final: payload.final ? dateUtils_formatDate(payload.final, { tableFormat: true }) : null,
      })
    },
    certificationWebpageProcessDate_CLEAR_DETAIL: (): void => {
      self.certificationWebpageProcessDate_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch process date detail
     *
     * @param {ICertificationWebpageProcessDateGetDetailAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageProcessDate_fetchDetail: flow(function* (payload: ICertificationWebpageProcessDateGetDetailAttrs) {
      self.certificationWebpageProcessDate_SET_LOADING({ type: BASE, value: true })

      const response: ICertificationWebpageProcessDateApiResponse =
        yield self.environment.certificationWebpageProcessDateApi.certificationWebpageProcessDate_getDetail({
          params: { ...payload.params },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpageProcessDate_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpageProcessDate_SET_LOADING({ type: BASE, value: false })
    }),

    /**
     * @description Update process detail
     *
     * @param {ICertificationWebpageProcessDateUpdateAttrs} payload
     *
     * @return {any} any
     */
    certificationWebpageProcessDate_update: flow(function* (payload: ICertificationWebpageProcessDateUpdateAttrs) {
      self.certificationWebpageProcessDate_SET_LOADING({ type: ACTION, value: true })

      const response: ICertificationWebpageProcessDateApiResponse =
        yield self.environment.certificationWebpageProcessDateApi.certificationWebpageProcessDate_update({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })

      // Do when response success
      if (response.ok) {
        self.certificationWebpageProcessDate_SET_DETAIL(response.res.data)

        if (payload?.onSuccess) payload.onSuccess()
      }

      // Do when response failed
      if (!response.ok) {
        if (payload?.onError) payload.onError()
      }

      self.certificationWebpageProcessDate_SET_LOADING({ type: ACTION, value: false })
    }),
  }))

export const createCertificationWebpageProcessDateDefaultModel = () => optional(CertificationWebpageProcessDateModel, {})
